/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { FormikProps } from 'formik'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Lightbox } from '@nexigendigital/nxui'
import { serviceAPI, useChangePlanMutation } from 'api/service'
import { InvoiceStatus } from 'models/enums'
import { ChangePlan } from 'models/service'
import { setInvoice } from 'store/slices/billingSlice'
import { setChangePlanTemplate } from 'store/slices/serviceSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { Timer } from 'helpers/utils'

import ChangeServicePlanForm from 'containers/services/form/changeServicePlanForm'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    id: number,
    status: boolean,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ChangeServicePlanLightbox({ status, close }: Props) {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const formRef = useRef<FormikProps<ChangePlan>>(null)
    const id = +location.pathname?.split('/')[2]
    const { changePlanTemplate, changePlanValue } = useAppSelector((state) => state.service)
    const { data: availablePlans } = serviceAPI.endpoints.availablePlans.useQueryState(id)
    const [changePlan, { data, isSuccess }] = useChangePlanMutation()
    const [isOptionChange, setIsOptionChange] = useState(false)
    const conditionalProps = useMemo(() => {
        switch (changePlanTemplate) {
            case 'confirm':
                return {
                    title: 'Confirm Change of Hosting Plan',
                    type: 'confirm',
                    icon: 'info',
                    description: `When you confirm this submission, an invoice for your hosting plan change will be generated. 
                    This invoice must be paid in full in order for the hosting plan change to take affect.`,
                    conditions: { state: status, action: () => close(), animate: true },
                    actions: [
                        {
                            label: 'No, return to Services',
                            color: 'error',
                            type: 'button',
                            func: () => {
                                close()
                            }
                        },
                        {
                            label: 'Generate Invoice',
                            color: 'primary',
                            type: 'submit',
                            func: () => {
                                const { id, billing_cycle_id, product_id, config } = changePlanValue
                                changePlan({
                                    id,
                                    preview: false,
                                    billing_cycle_id,
                                    product_id,
                                    config
                                })
                            }
                        }
                    ]
                }
            case 'configure':
            default:
                return {
                    title: 'Change Hosting Plan',
                    description: `You can change your hosting plan in order to update your resource requirements. 
                    You will be invoiced on the new hosting plan at the end of your current prepaid period.`,
                    content: <ChangeServicePlanForm form={formRef} close={close} isOptionChange={isOptionChange} setIsOptionChange={setIsOptionChange}/>,
                    conditions: { state: status, action: () => close() },
                    links: [],
                    actions: [
                        {
                            label: 'Back',
                            color: 'secondary',
                            func: () => close()
                        },
                        {
                            label: 'Change',
                            color: 'primary',
                            type: 'submit',
                            disabled: !isOptionChange && !(availablePlans && availablePlans.plan_paths.length >= 1),
                            form: 'changePlanForm'
                        }
                    ]
                }
        }
    }, [isOptionChange, changePlanTemplate, status])

    useEffect(() => {
        if (isSuccess && data && data.status === InvoiceStatus.Unpaid) {
            dispatch(setInvoice(data.id))
        }

        Timer(() => {
            dispatch(setChangePlanTemplate('configure'))
        }, 250)

        close()
    }, [data, isSuccess])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return <Lightbox {...conditionalProps} />
}
